<template>
  <div v-bind="$attrs" class="relative" ref="wrapper">
    {{ error }}
    <template v-if="itemList.length === 0 && !pending && !earlierPostsLoading">
      <Card v-if="noPostsMessage" class="text-center">
        <AsyncImage :src="noPostsIconLink" class="h-48 w-auto mx-auto mb-4" />
        {{ noPostsMessage }}
      </Card>
    </template>
    <Card v-if="newPosts" class="text-center !p-0 mb-4">
      <NamiButton button-type="primary" text block @click="refreshAllPosts">
        Load new posts
      </NamiButton>
    </Card>
    <template v-for="(item, index) in visibleItemList" :key="item.id">
      <div
        class="absolute left-0 w-full"
        :style="{ transform: `translateY(${item.offset}px)` }"
      >
        <FeedPostSkeleton
          v-if="!item.post"
          :class="{
            'border-t-8 sm:border-t-0 border-neutral-200 dark:border-black':
              indexOffset + index > 0,
          }"
          :embed="item.post === undefined"
          @size="(height) => updateItemHeight(indexOffset + index, height)"
        />
        <FeedPostVirtual
          v-else
          :class="{
            'border-t-8 sm:border-t-0 border-neutral-200 dark:border-black':
              indexOffset + index > 0,
          }"
          :feedItem="item.post"
          v-model:comments-open="item.commentsOpen"
          v-model:is-replying="item.isReplying"
          @copy="copyPostUrl"
          @deleted="() => deleteItem(indexOffset + index)"
          @doReport="initiateReport"
          @showReactions="initiateReactions"
          @showReposts="initiateReposts"
          @size="(height) => updateItemHeight(indexOffset + index, height)"
          :externalLoading="isPerformingAction"
        />
      </div>
    </template>
  </div>
  <ReportModal
    v-model="showReportModal"
    entityType="post"
    :entityId="selectedPostId!"
  />
  <FeedPostReactionsModal
    v-model="showReactionsModal"
    :entity-id="selectedPostId"
    no-cache
    entity-type="post"
  />
  <FeedPostRepostsModal
    v-model="showListRepostsModal"
    :postId="selectedPostId"
  />
</template>

<script setup lang="ts">
import {
  type Collection,
  type PostEntity,
  type PostListParams,
} from "~/src/api";
import { nanoid } from "nanoid";
import FeedPostVirtual from "~/components/feed/post/FeedPostVirtual.vue";
import {
  copyPostUrl,
  generateSkeletonPosts,
  type FeedPostType,
  POST_INCLUDES,
} from "~/src/feed/postUtils";
import { useVirtualizedList } from "~/composables/feed/virtualizationManager";

interface Props {
  fetchNext: (params: PostListParams) => Promise<Collection<PostEntity>>;
  noPostsMessage?: string;
  noPostsIconLink?: string;
  realm?: string;
}

const props = defineProps<Props>();

// This should be a client only component
if (!process.client) {
  throw new Error("This component must be rendered on the client only.");
}

const { getBatch } = usePostSessionCache();

const PRELOAD_BUFFER = 5;
const INITIAL_LOAD_BUFFER = 10;
const SUBSEQUENT_LOAD_BUFFER = 5;

const wrapper = ref<HTMLDivElement>();

// post virtualization manager
const { updateItemHeight, deleteItem, itemList, visibleItemList, indexOffset } =
  useVirtualizedList<FeedPostType>(wrapper, document, fetchEarlierPosts);

itemList.value = generateSkeletonPosts(PRELOAD_BUFFER);
const totalPosts = ref(0);

//TODO: remove once algo
const latestDateIndex = ref<string>();

const earliestDateIndex = ref<string>();
const newPosts = ref(0);
const deduped = ref(0);
const dedupeReposts = (inputArr: FeedPostType[]) => {
  if (inputArr.length === 0) return [];
  const posts = inputArr.filter((p) => p.post);
  latestDateIndex.value =
    posts[posts.length - 1].post!.repostData?.originalPostTimestamp ??
    posts[posts.length - 1].post!.timestamp!;
  const clone: FeedPostType[] = JSON.parse(JSON.stringify(inputArr));
  // return clone
  const dedupedArr: FeedPostType[] = [];
  for (let i = 0; i < clone.length; i++) {
    let curr = clone[i];
    if (!clone[i].post) continue;
    for (let j = i + 1; j < clone.length; j++) {
      if (
        clone[j].post &&
        clone[j].post!.postId === clone[i].post!.postId &&
        clone[j].post!.repostData?.repostedBy.length
      ) {
        const allReposters = (curr.post!.repostData?.repostedBy ?? []).concat(
          clone[j].post!.repostData!.repostedBy,
        );
        const uniqueUsers = [...new Set(allReposters.map((u) => u.id))];
        curr.post!.repostData!.repostedBy = uniqueUsers.map(
          (id) => allReposters.find((u) => u.id === id)!,
        );
        const removed = clone.splice(j, 1)[0];
        console.debug(
          "Repost with id",
          removed.post!.repostData?.originalPostId,
          "removed.",
        );
        deduped.value++;
        j--;
      }
    }
    dedupedArr.push(curr);
  }

  return dedupedArr;
};

// this executes client only
async function loadPosts(limit: number = INITIAL_LOAD_BUFFER, before?: string) {
  const response = await props.fetchNext({
    limit: limit,
    publishedAtBefore: before,
    order: {
      publishedAt: "desc",
    },
    includes: POST_INCLUDES,
    includeReposts: true,
    showDeleted: false,
    realm: props.realm,
  });

  const populated = await getBatch(response.data);

  return {
    posts: populated.map<FeedPostType>((cachePost) => ({
      id: nanoid(),
      post: cachePost,
      offset: 0,
      size: 0,
      commentsOpen: false,
      isReplying: false,
      loading: false,
    })),
    meta: {
      ...response.meta,
      total: response.meta.total,
    },
  };
}

const { pending, refresh, error } = useLazyAsyncData(
  async () => {
    const { posts, meta } = await loadPosts(INITIAL_LOAD_BUFFER);

    totalPosts.value = meta.total;
    earliestDateIndex.value =
      posts[0]?.post?.repostData?.originalPostTimestamp ??
      posts[0]?.post?.timestamp;
    itemList.value = dedupeReposts(posts);

    if (meta.total - deduped.value > INITIAL_LOAD_BUFFER) {
      const skeleton = generateSkeletonPosts(PRELOAD_BUFFER);
      itemList.value.push(...skeleton);
    }
  },
  { server: false },
);

const earlierPostsLoading = ref(false);

async function fetchEarlierPosts() {
  if (
    earlierPostsLoading.value ||
    !itemList.value.filter((p) => !!p.post).length
  )
    return;
  const currentPostsTotal = itemList.value.filter((p) => !!p.post).length;
  console.debug(
    "Fetching earlier posts.\nCurrent:",
    currentPostsTotal,
    "\nTotal:",
    totalPosts.value,
  );
  earlierPostsLoading.value = true;

  // TODO: restore when algo
  // const lastPost = itemList.value
  //   .filter((p) => !!p.post)
  //   .at(currentPostsTotal - 1);
  // const earliestTimestamp = lastPost
  //   ? lastPost.post!.repostTimestamp ?? lastPost.post!.timestamp!
  //   : "";
  const earliestTimestamp = latestDateIndex.value!;

  const { posts, meta } = await loadPosts(
    SUBSEQUENT_LOAD_BUFFER + 1,
    encodeURIComponent(earliestTimestamp),
  );

  const skeleton = generateSkeletonPosts(PRELOAD_BUFFER);

  const skeletons = itemList.value.splice(
    itemList.value.length - PRELOAD_BUFFER,
    PRELOAD_BUFFER,
  );

  totalPosts.value = Math.max(meta.total, totalPosts.value);

  itemList.value = dedupeReposts(
    itemList.value.concat(
      posts.slice(1).map((p, index) => ({
        ...p,
        id: skeletons[index] ? skeletons[index].id : nanoid(),
        loading: false,
      })),
    ),
  );

  if (
    itemList.value.filter((p) => !!p.post).length <
    totalPosts.value - deduped.value
  )
    itemList.value.push(...skeleton);

  console.debug(
    "Posts fetched.\nCurrent:",
    itemList.value.filter((p) => !!p.post).length,
    "\nTotal:",
    totalPosts.value,
    "\nDeduped:",
    deduped.value,
  );
  earlierPostsLoading.value = false;
}

// per post options
const showReportModal = ref(false);
const showListRepostsModal = ref(false);
const showReactionsModal = ref(false);
const isPerformingAction = ref(false);

const selectedPostId = ref<string | null>();

function initiateReport(id: string) {
  selectedPostId.value = id;
  showReportModal.value = true;
}

function initiateReposts(id: string) {
  selectedPostId.value = id;
  showListRepostsModal.value = true;
}

function initiateReactions(id: string) {
  selectedPostId.value = id;
  showReactionsModal.value = true;
}

async function checkForNewPosts() {
  if (!earliestDateIndex.value || newPosts.value) return;
  const res = await props.fetchNext({
    limit: 0,
    publishedAtSince: encodeURIComponent(earliestDateIndex.value),
  });

  // subtract 1 since its inclusive
  newPosts.value = res.meta.total - 1;
}

async function refreshAllPosts() {
  itemList.value = generateSkeletonPosts(PRELOAD_BUFFER);
  earliestDateIndex.value = undefined;
  newPosts.value = 0;
  await refresh();
}

defineExpose({
  checkForNewPosts,
  refreshAllPosts,
});
</script>

<style lang="postcss">
.animate-in {
  animation: fadeIn 500ms;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
